import { useCallback } from "react";
import { axiosInstance } from "@/lib/axios";
import toast from "react-hot-toast";
import { useAuth } from "./useAuth";
import {
  useQuizVisibility,
  useQuizAnswers,
  useQuizProgress,
} from "./useQuizState";

export const useQuiz = () => {
  const { refreshUserData } = useAuth();
  
  // Use smaller, focused hooks for state management
  const {
    showQuiz,
    setShowQuiz,
    quiz,
    setQuiz,
    quizError,
    setQuizError,
  } = useQuizVisibility();

  const {
    quizAnswers,
    setQuizAnswers,
    quizResult,
    setQuizResult,
    isSubmittingQuiz,
    setIsSubmittingQuiz,
  } = useQuizAnswers();

  const {
    progress,
    setProgress,
    currentQuizQuestionIdx,
    setCurrentQuizQuestionIdx,
    quizStartTime,
    setQuizStartTime,
  } = useQuizProgress();

  // Fetch quiz for selected tutorial
  const fetchQuiz = useCallback(async (courseId: string, tutorialId: string) => {
    try {
      console.log("Fetching quiz for course:", courseId, "tutorial:", tutorialId);
      // Try to fetch quiz from API first
      const res = await axiosInstance.get(
        `/api/v1/quizzes?tutorialId=${tutorialId}`
      );
      console.log("Quiz fetch response:", res.data);
      const quizData = res.data.data.document[0];
      console.log("Setting quiz data:", quizData);
      startQuiz(quizData);
    } catch (err) {
      console.error("Error fetching quiz from API:", err);
      // If API fails, show error message
      setQuizError("Quiz not available. Please try again later.");
      setQuiz(null);
      setShowQuiz(false);
    }
  }, []);

  // Fetch user progress for selected tutorial
  const fetchProgress = useCallback(async (courseId: string, tutorialId: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/quiz-results/progress/${courseId}/${tutorialId}`
      );
      setProgress(res.data.data);
    } catch (err) {
      console.log("Progress endpoint not available, setting progress to null");
      setProgress(null);
    }
  }, []);

  // Check if user has already taken the quiz
  const checkQuizResult = useCallback(async (courseId: string, tutorialId: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/quiz-results/${courseId}/${tutorialId}`
      );
      if (res.data.data) {
        setQuizResult({ ...res.data.data, success: true });
        setShowQuiz(false);
        return true; // User has already taken the quiz
      }
    } catch (err) {
      console.log("Quiz result checking not available, assuming user hasn't taken quiz");
    }
    return false; // User hasn't taken the quiz yet
  }, []);

  // Handle quiz answer change
  const handleQuizAnswer = useCallback((questionIdx: number, selectedAnswer: string) => {
    console.log(`Setting answer for question ${questionIdx}:`, selectedAnswer);
    setQuizAnswers((prev) => {
      const updated = [...prev];
      const currentQuestion = quiz.questions[questionIdx];

      console.log("Current question structure:", currentQuestion);
      console.log("Current question options:", currentQuestion.options);
      console.log("Options type:", typeof currentQuestion.options);
      console.log("Is options array:", Array.isArray(currentQuestion.options));

      // Find the correct answer for this question
      let isCorrect = false;
      if (Array.isArray(currentQuestion.options)) {
        const correctAnswer = currentQuestion.options.find((option: any) => option.isCorrect);
        isCorrect = correctAnswer?.text?.en === selectedAnswer || correctAnswer?.text === selectedAnswer;
      } else if (currentQuestion.options && typeof currentQuestion.options === 'object') {
        // Handle case where options might be an object instead of array
        const optionsArray = Object.values(currentQuestion.options) as any[];
        const correctAnswer = optionsArray.find((option: any) => option.isCorrect);
        isCorrect = correctAnswer?.text?.en === selectedAnswer || correctAnswer?.text === selectedAnswer;
      }

      // Create the answer object in the format expected by the backend
      updated[questionIdx] = {
        questionId: currentQuestion._id,
        selectedAnswer: {
          en: selectedAnswer,
          am: selectedAnswer, // You might want to add proper translations here
        },
        isCorrect: isCorrect,
      };

      console.log("Updated quiz answers:", updated);
      return updated;
    });
  }, [quiz]);

  // Submit quiz answers
  const handleQuizSubmit = useCallback(async (
    user: any,
    course: any,
    selectedTutorial: any
  ) => {
    // Check for unanswered questions
    if (!quiz || !quiz.questions) return;
    const unanswered = quiz.questions.findIndex(
      (_: any, idx: number) => {
        const selectedAnswer = quizAnswers[idx]?.selectedAnswer;
        return typeof selectedAnswer === 'string' 
          ? !selectedAnswer 
          : !(selectedAnswer?.en || selectedAnswer?.am);
      }
    );
    if (unanswered !== -1) {
      setQuizError("Please fill all answers before submitting the quiz.");
      return;
    }
    setQuizError("");

    // Calculate duration - use time difference between start and now
    const durationInSeconds = quizStartTime
      ? Math.max(Math.floor((Date.now() - quizStartTime) / 1000), 1)
      : 60; // Minimum 60 seconds

    setIsSubmittingQuiz(true);

    // Calculate score and percentage
    const correctAnswers = quizAnswers.filter(answer => answer.isCorrect).length;
    const totalQuestions = quizAnswers.length;
    const score = correctAnswers;
    const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

    // Log the payload for debugging
    const payload = {
      quizId: quiz._id,
      companyId: user?.companyId || "default-company-id",
      employeeId: (user as any)?._id || user?.id || "default-employee-id",
      courseId: course?.document?._id,
      tutorialId: selectedTutorial._id,
      answers: quizAnswers.map(answer => ({
        questionId: answer.questionId,
        selectedAnswer: answer.selectedAnswer,
        isCorrect: answer.isCorrect
      })),
      score: score,
      percentage: percentage,
      durationInSeconds: durationInSeconds,
      feedback: percentage >= 80 
        ? "Great job! You've correctly answered most questions about this topic."
        : percentage >= 60
        ? "Good effort! Consider reviewing the material and trying again."
        : "Keep studying! Review the course material and try again."
    };

    console.log("Submitting quiz with payload:", payload);

    try {
      const res = await axiosInstance.post("/api/v1/quiz-results", payload, {
        withCredentials: true,
      });
      console.log("Quiz submission successful:", res.data);
      setQuizResult({ ...res.data.data, success: true });
      setShowQuiz(false);
      fetchProgress(course?.document?._id, selectedTutorial._id);
      
      // Refresh user data to update progress and scores
      await refreshUserData();
    } catch (err: unknown) {
      const error = err as any;
      console.error("Quiz submit error:", error);

      // Check if user has already taken the test
      if (
        error.response?.status === 409 ||
        error.response?.data?.message?.includes("already taken")
      ) {
        // User has already taken the test, show their previous result
        if (error.response?.data?.data) {
          setQuizResult({ ...error.response.data.data, success: true });
          setShowQuiz(false);
        } else {
          setQuizResult({
            error:
              "You have already taken this quiz. Check your previous results.",
            success: false,
          });
        }
      } else {
        let errorMsg = "Failed to submit quiz.";
        if (error.response?.status === 404) {
          errorMsg = "Quiz submission not available. Please try again later.";
        } else if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMsg = error.response.data.message;
        }
        setQuizResult({ error: errorMsg, success: false });
      }
    } finally {
      setIsSubmittingQuiz(false);
    }
  }, [quiz, quizAnswers, quizStartTime, fetchProgress]);

  // Start quiz
  const startQuiz = useCallback((quizData: any) => {
    setQuiz(quizData);
    setShowQuiz(true);
    setQuizStartTime(Date.now());
    setCurrentQuizQuestionIdx(0);
  }, []);

  // Reset quiz state
  const resetQuiz = useCallback(() => {
    setQuiz(null);
    setQuizAnswers([]);
    setQuizResult(null);
    setShowQuiz(false);
    setProgress(null);
    setCurrentQuizQuestionIdx(0);
    setQuizError("");
    setQuizStartTime(null);
    setIsSubmittingQuiz(false);
  }, []);

  // Navigate to previous question
  const goToPreviousQuestion = useCallback(() => {
    setCurrentQuizQuestionIdx((idx) => Math.max(0, idx - 1));
  }, []);

  // Navigate to next question
  const goToNextQuestion = useCallback(() => {
    const selectedAnswer = quizAnswers[currentQuizQuestionIdx]?.selectedAnswer;
    const hasAnswer = typeof selectedAnswer === 'string' 
      ? !!selectedAnswer 
      : !!(selectedAnswer?.en || selectedAnswer?.am);
    
    if (hasAnswer) {
      setCurrentQuizQuestionIdx((idx) =>
        Math.min(quiz.questions.length - 1, idx + 1)
      );
    } else {
      toast.error("Please select an answer before proceeding to the next question.");
    }
  }, [currentQuizQuestionIdx, quizAnswers, quiz]);

  return {
    // State
    showQuiz,
    quiz,
    quizError,
    quizAnswers,
    quizResult,
    progress,
    currentQuizQuestionIdx,
    isSubmittingQuiz,
    quizStartTime,
    
    // Actions
    fetchQuiz,
    fetchProgress,
    checkQuizResult,
    handleQuizAnswer,
    handleQuizSubmit,
    startQuiz,
    resetQuiz,
    goToPreviousQuestion,
    goToNextQuestion,
    
    // Setters
    setShowQuiz,
    setQuiz,
    setQuizError,
    setQuizAnswers,
    setQuizResult,
    setProgress,
    setCurrentQuizQuestionIdx,
    setIsSubmittingQuiz,
    setQuizStartTime,
  };
};
